import { TrimPrintManager, TrimPrint } from '../../../print/trim.print';
import { initPictureFlags, TrimImageController } from '../image/trim.image-controller';
import { TrimEditableImageManager } from '../../editable-image/trim.editable-image.manager';
import { TrimImageEvent } from '../image/trim.image-event';
import { TrimImageEditableTool } from '../image/trim.image-editable-tool';
import { TrimDataManager } from '../../data/trim-data.manager';
import { OrderFrameType, OrderPictureType } from '../../../../models/api/front/layout';
import { TrimCjBitmap, TrimCjContainer, TrimCjShape } from '../../../model/cj-factory';
import {
  CJ_BGIMAGE_NAME,
  CJ_CONTAINER_NAME,
  CJ_DPOBJ_NAME,
  CJ_FRAME_NAME,
} from '../../../model/cj-factory/trim.cj-obj-name.collection';
import { TrimCjGraphics } from '../../../model/cj-factory/trim.cj-graphics';
import { OrderPageDataXml } from '../../../../xml/class/order/order-page-data-xml';
import lodash, { cloneDeep } from 'lodash';
import { TrimAdditionalManager } from '../../additional/trim.additional.manager';
import { TrimCjTool } from '../../../../utilities/trim.cj-tool';
import { TrimImageEditManager } from '../trim.image-edit.manager';
import { TrimEditableEvent } from '../trim.editable-event';
import { TrimUiManager } from '../../ui/trim.ui.manager';
import { TrimEditableSideTool } from '../trim.editable-side-tool';
import { TrimTextController } from '../text/trim.text-controller';
import { PrintBase } from '../../../model/trim.print-model';
import { ScreenColorCodeType } from '../../../../layout-editor/manager/image-edit/image-edit.manager';
import PixelConverter from '../../../../utilities/pixel-converter';

export type HighlightType = {
  pageId?: string;
  photoAreaId?: string;
  category?: 'highlight';
} & TrimCjShape
export type WindowType = {
  pageId?: string;
  photoAreaId?: string;
  category?: 'window';
} & TrimCjShape

// フチなしの商品判別用
export const NO_EDGE_TYPE = 'jptg382204';

export class TrimFrameController {
  private stage: TrimCjContainer;
  private readonly pageId: string;
  public orderPage?: OrderPageDataXml;

  private printManager!: TrimPrintManager;
  private editableImageManager!: TrimEditableImageManager;
  private imageController!: TrimImageController;
  private imageEvent!: TrimImageEvent;
  private imageEditableTool!: TrimImageEditableTool;
  private trimDataManager!: TrimDataManager;
  private additionalManager!: TrimAdditionalManager;
  private imageEditManager!: TrimImageEditManager;
  private editableEvent!: TrimEditableEvent;
  private editableSideTool!: TrimEditableSideTool;
  private textController!: TrimTextController;

  constructor(
    stage: TrimCjContainer,
    pageId: string,
  ) {
    this.stage = stage;
    this.pageId = pageId;
  }

  di(
    imageController: TrimImageController,
    printMng: TrimPrintManager,
    editableMng: TrimEditableImageManager,
    imageEvent: TrimImageEvent,
    imageEditableTool: TrimImageEditableTool,
    layoutStoreMng: TrimDataManager,
    additionalMng: TrimAdditionalManager,
    imageEditMng: TrimImageEditManager,
    editableEvent: TrimEditableEvent,
    editableSideTool: TrimEditableSideTool,
    textController: TrimTextController,
  ): void {
    this.imageController = imageController;
    this.printManager = printMng;
    this.editableImageManager = editableMng;
    this.imageEvent = imageEvent;
    this.imageEditableTool = imageEditableTool;
    this.trimDataManager = layoutStoreMng;
    this.additionalManager = additionalMng;
    this.imageEditManager = imageEditMng;
    this.editableEvent = editableEvent;
    this.editableSideTool = editableSideTool;
    this.textController = textController;
  }

  initialize(orderPage?: OrderPageDataXml) {
    this.orderPage = orderPage;
  }

  destroy() {
  }

  update(stage: TrimCjContainer): void {
    this.stage = stage;
  }

  // - ガイド -
  createGuide() {

    const print = this.printManager.currentPrint;
    const base = print?.base.getImageWindow();
    const fills = print?.base.getFillWindow();
    const _guideWidth = 1.5;
    // NOTE: フチなしの商品と白フチ商品の判別がテンプレートではできないためハードコーディング
    const isNoEdge = print?.item.productData?.meta?.borderType === NO_EDGE_TYPE;
    if (!base) return;
    // const edgeTmp = this.printManager.getEdge(base.virtual.wh, fills?.length ? fills : []);
    const edgeTmp = isNoEdge ? undefined : this.printManager.getEdge(base.virtual.wh, fills?.length ? fills : []);

    const guideWidth = PixelConverter.mmToPx(_guideWidth) / 2;
    const guidePos = {
      x: isNoEdge ? guideWidth/2 + base.virtual.pos.x : guideWidth/2,
      y: isNoEdge ? guideWidth/2 + base.virtual.pos.y : guideWidth/2,
    }
    const container = new TrimCjContainer();
    container.name = CJ_CONTAINER_NAME.guide;
    const shape = new TrimCjShape();
    const shapeLine = new TrimCjShape();
    shape.graphics.setStrokeStyle(guideWidth).beginStroke('rgba(0, 0, 0, 0.7)').dr(guidePos.x, guidePos.y, this.imageEditManager.templateBaseVirtual.w - guideWidth, this.imageEditManager.templateBaseVirtual.h - guideWidth);
    shapeLine.graphics.beginStroke('rgba(255, 0, 0, 0.4)').dr(guidePos.x, guidePos.y, this.imageEditManager.templateBaseVirtual.w - guideWidth, this.imageEditManager.templateBaseVirtual.h - guideWidth);
    shape.name = CJ_BGIMAGE_NAME.guide;
    shapeLine.name = CJ_BGIMAGE_NAME.guideLine;
    container.set({ x: edgeTmp?.virtual.pos.x ?? 0, y: edgeTmp?.virtual.pos.y ?? 0 });
    if(this.imageEditManager.printMenu.modeAbleArea === 'off') {
      container.visible = false;
    }
    container.addChild(shape, shapeLine);
    this.stage.addChild(container);
  }

  // - マスク -
  createBgImage() {
    const print = this.printManager.currentPrint;
    const base = print?.base.getImageWindow();
    if (!base) {
      throw new Error('printModel is notFound !!!!!!!!!');
    }
    const { virtual } = base;
    const { pos, wh } = virtual;
    const container = new TrimCjContainer();
    container.name = CJ_CONTAINER_NAME.base;
    const shape = new TrimCjShape();
    shape.graphics.f('#d30f7c').dr(0, 0, wh.width, wh.height);
    shape.name = CJ_BGIMAGE_NAME.base;
    shape.setBounds(0, 0, wh.width, wh.height);
    container.set({ x: pos.x, y: pos.y });
    container.addChild(shape);
    this.stage.addChild(container);
  }

  createEdge() {
    const print = this.printManager.currentPrint;
    const base = print?.base.getImageWindow();
    const fills = print?.base.getFillWindow();
    // NOTE: フチなしの商品と白フチ商品の判別がテンプレートではできないためハードコーディング
    const isNoEdge = print?.item.productData?.meta?.borderType === NO_EDGE_TYPE;
    if (!base || isNoEdge) return;

    const edgeTmp = this.printManager.getEdge(base.virtual.wh, (fills ?? []));
    const container = new TrimCjContainer();
    container.name = CJ_CONTAINER_NAME.edge;
    const shape = new TrimCjShape();
    if (edgeTmp) {
      const { virtual } = edgeTmp;
      const { pos, wh } = virtual;
      // shape.graphics.f(  '#fff').dr(pos.x, pos.y, wh.width, wh.height);
      shape.graphics.f(edgeTmp.fill?.color ?? '#fff').dr(0, 0, wh.width, wh.height);
      shape.name = CJ_BGIMAGE_NAME.edge;
      shape.setBounds(0, 0, wh.width, wh.height);
      container.set({ x: pos.x, y: pos.y });
    } else {
      const { virtual } = base;
      const { pos, wh } = virtual;
      const width = wh.width + (pos.x * 2);
      const height = wh.height + (pos.y * 2);
      // shape.graphics.f(  '#fff').dr(pos.x, pos.y, wh.width, wh.height);
      shape.graphics.f( '#fff').dr(0, 0, width, height);
      shape.name = CJ_BGIMAGE_NAME.edge;
      shape.setBounds(0, 0, width, height);
    }
      container.addChild(shape);
      this.stage.addChild(container);
  }

  // - マスク -
  createMask(): TrimCjShape {
    const print = this.printManager.currentPrint;
    const base = print?.base.getImageWindow();
    if (!base) {
      throw new Error('printModel is notFound !!!!!!!!!');
    }
    const { virtual } = base;
    const { pos, wh } = virtual;
    const shape = new TrimCjShape();
    shape.graphics.f('#d30f7c').dr(0, 0, wh.width, wh.height);
    shape.name = CJ_FRAME_NAME.mask;
    shape.setBounds(0, 0, wh.width, wh.height);
    return shape;
  }

  async createGrayOut() {
    const grayOut = new TrimCjShape();
    grayOut.name = CJ_BGIMAGE_NAME.grayout;
    // grayOut.graphics.f('#000').dr(0, 0, Number(this.albumData.virtualWidth), Number(this.albumData.virtualHeight));
    grayOut.alpha = 0.4;
    grayOut.visible = false;
    this.stage?.addChild(grayOut);
  }

  // - 写真枠作成 -
  createPhotoArea = async () => {
    const printBase = this.printManager.currentPrint?.base;
    const printFrame = printBase?.getImageWindow();
    if (!printFrame) return;
    const imageAreaContainer = new TrimCjContainer();

    const editorContainer = new TrimCjContainer();
    editorContainer.set({
      name: CJ_CONTAINER_NAME.editor,
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      regX: printFrame.virtual.pos.x,
      regY: printFrame.virtual.pos.y,
    });
    editorContainer.virtual = {
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      width: printFrame.virtual.wh.width,
      height: printFrame.virtual.wh.height,
    };
    const scaleContainer = new TrimCjContainer();
    scaleContainer.set({
      name: CJ_CONTAINER_NAME.scale,
      x: printFrame.virtual.pos.x + printFrame.virtual.wh.width / 2,
      y: printFrame.virtual.pos.y + printFrame.virtual.wh.height / 2,
      regX: printFrame.virtual.pos.x + printFrame.virtual.wh.width / 2,
      regY: printFrame.virtual.pos.y + printFrame.virtual.wh.height / 2,
      scaleX: 1,
      scaleY: 1,
    });
    scaleContainer.virtual = {
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      width: printFrame.virtual.wh.width,
      height: printFrame.virtual.wh.height,
    };
    const rotateContainer = new TrimCjContainer();
    rotateContainer.set({
      name: CJ_CONTAINER_NAME.rotate,
      x: printFrame.virtual.pos.x + printFrame.virtual.wh.width / 2,
      y: printFrame.virtual.pos.y + printFrame.virtual.wh.height / 2,
      regX: printFrame.virtual.pos.x + printFrame.virtual.wh.width / 2,
      regY: printFrame.virtual.pos.y + printFrame.virtual.wh.height / 2,
      rotation: 0,
      isRotate: false,
    });
    rotateContainer.virtual = {
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      width: printFrame.virtual.wh.width,
      height: printFrame.virtual.wh.height,
    };
    editorContainer.addChild(scaleContainer);
    scaleContainer.addChild(rotateContainer);
    imageAreaContainer.set({
      name: CJ_CONTAINER_NAME.imageArea,
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      regX: printFrame.virtual.pos.x,
      regY: printFrame.virtual.pos.y,

    });
    imageAreaContainer.virtual = {
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      width: printFrame.virtual.wh.width,
      height: printFrame.virtual.wh.height,
    };
    // 写真枠の背景


    // back = await TrimImageController.createImageAsync(backPath, CJ_FRAME_NAME.back);
    // const back = new TrimCjContainer();
    // back.name = CJ_FRAME_NAME.back;
    // const mask = this.createMask();
    // back.addChild(mask);
    // back.set({
    //   x: printFrame.virtual.pos.x,
    //   y: printFrame.virtual.pos.y,
    //   width: printFrame.virtual.wh.width,
    //   height: printFrame.virtual.wh.height,
    // });
    // rotateContainer.addChild(back);
    // 写真枠のマスク

    // const mask = await TrimImageController.createImageAsync(maskPath, CJ_FRAME_NAME.mask);
    const imageMask = new TrimCjContainer();
    imageMask.name = CJ_CONTAINER_NAME.mask;
    const mask = this.createMask();
    imageMask.addChild(mask);
    imageAreaContainer.addChild(imageMask);
    imageMask.set({
      x: printFrame.virtual.pos.x,
      y: printFrame.virtual.pos.y,
      width: printFrame.virtual.wh.width,
      height: printFrame.virtual.wh.height,
      alpha: 1,
    });
    const hitArea = new TrimCjShape();
    hitArea.set({
      x: hitArea.x,
      y: hitArea.y,
      graphics: new TrimCjGraphics().f('#FFFFFF').dr(0, 0, printFrame.virtual.wh.width, printFrame.virtual.wh.height),
    });
    imageAreaContainer.hitArea = hitArea;

    // 写真移動時の境界線表示用DummyShape(左上から時計回りにA,B,C,D)
    const dummyFrame = new TrimCjContainer();
    const dummyRect = new TrimCjShape();
    const dummyCornerA = new TrimCjShape();
    const dummyCornerB = new TrimCjShape();
    const dummyCornerC = new TrimCjShape();
    const dummyCornerD = new TrimCjShape();
    dummyRect.graphics.f('#00f').dr(printFrame.virtual.pos.x, printFrame.virtual.pos.y, printFrame.virtual.wh.width, printFrame.virtual.wh.height);
    dummyCornerA.graphics.s('#00f').dc(printFrame.virtual.pos.x, printFrame.virtual.pos.y, 2);
    dummyCornerB.graphics.s('#00f').dc(printFrame.virtual.pos.x + printFrame.virtual.wh.width, printFrame.virtual.pos.y, 2);
    dummyCornerC.graphics.s('#00f').dc(printFrame.virtual.pos.x + printFrame.virtual.wh.width, printFrame.virtual.pos.y + printFrame.virtual.wh.height, 2);
    dummyCornerD.graphics.s('#00f').dc(printFrame.virtual.pos.x, printFrame.virtual.pos.y + printFrame.virtual.wh.height, 2);
    dummyRect.name = CJ_DPOBJ_NAME.dummyRect;
    dummyCornerA.name = CJ_DPOBJ_NAME.dummyCornerA;
    dummyCornerA.alpha = 0.01;
    dummyCornerB.name = CJ_DPOBJ_NAME.dummyCornerB;
    dummyCornerB.alpha = 0.01;
    dummyCornerC.name = CJ_DPOBJ_NAME.dummyCornerC;
    dummyCornerC.alpha = 0.01;
    dummyCornerD.name = CJ_DPOBJ_NAME.dummyCornerD;
    dummyCornerD.alpha = 0.01;
    dummyFrame.alpha = 0;
    dummyFrame.name = CJ_CONTAINER_NAME.dummyFrame;
    dummyFrame.addChild(dummyRect, dummyCornerA, dummyCornerB, dummyCornerC, dummyCornerD);
    imageAreaContainer.addChild(dummyFrame);

    rotateContainer.addChild(imageAreaContainer);
    // highlightの生成
    const highlight: HighlightType = new TrimCjShape();
    highlight.name = CJ_DPOBJ_NAME.highlight;
    highlight.graphics.f('#fff').dr(printFrame.virtual.pos.x, printFrame.virtual.pos.y, printFrame.virtual.wh.width, printFrame.virtual.wh.height);
    highlight.alpha = 0.4;
    highlight.visible = false;
    imageAreaContainer.addChild(highlight);
    // grayFilmの生成
    const grayFilm: HighlightType = new TrimCjShape();
    grayFilm.name = CJ_DPOBJ_NAME.grayFilm;
    grayFilm.graphics.f('#000').dr(printFrame.virtual.pos.x, printFrame.virtual.pos.y, printFrame.virtual.wh.width, printFrame.virtual.wh.height);
    grayFilm.alpha = 0.4;
    grayFilm.visible = false;
    imageAreaContainer.addChild(grayFilm);

    this.stage.addChild(editorContainer);
    this.trimDataManager.setEditor(editorContainer);
    this.imageEvent.setImageEvent(editorContainer, 'frame');
    if (!this.printManager.editableImage) return;
    await this.imageController.createPhotoImage(this.printManager.editableImage, editorContainer);
  };

  async createStageMask(color: ScreenColorCodeType = '#000000') {
    const print = this.printManager.currentPrint;
    const tempBase = print?.base.getImageWindow();
    // NOTE: フチなしの商品と白フチ商品の判別がテンプレートではできないためハードコーディング
    const isNoEdge = print?.item.productData?.meta?.borderType === NO_EDGE_TYPE;
    if (!tempBase) return;

    const base = {
      x: -TrimImageEditManager.STAGE_CONTAINER_X,
      y: -TrimImageEditManager.STAGE_CONTAINER_Y,
      w: TrimImageEditManager.CANVAS_W,
      h: TrimImageEditManager.CANVAS_H,
    };
    const window = {
      x: isNoEdge ? tempBase.virtual.pos.x : 0,
      y: isNoEdge ? tempBase.virtual.pos.y : 0,
      w: this.imageEditManager.templateBaseVirtual.w,
      h: this.imageEditManager.templateBaseVirtual.h,
    };
    // console.log(base);
    // console.log(window);

    this.additionalManager.drawBase(base, color);
    this.additionalManager.clipWindow(base, window, 0, 'rect');
    const baseMaskContainer = new TrimCjContainer();
    baseMaskContainer.pageId = this.pageId;
    baseMaskContainer.name = CJ_CONTAINER_NAME.baseMask;
    const baseMaskUrl = this.additionalManager.makeAdditional();
    const baseMaskWindow = new TrimCjBitmap(baseMaskUrl);
    baseMaskWindow.name = `${CJ_DPOBJ_NAME.baseMaskWindow}`;
    baseMaskContainer.addChild(baseMaskWindow);
    baseMaskContainer.set({
      x: base.x,
      y: base.y,
      alpha: 1,
      mouseEnable: false,
    });
    this.stage.addChild(baseMaskContainer);
    console.log(this.stage);
  }

}
